import React, { createContext, useContext, useState } from 'react';
import apiService from '../services/api';

const ActionContext = createContext(null);

// Helper function for adding delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const ActionProvider = ({ children }) => {
  // Variables for preventing multiple API calls
  let isRefreshing = false;
  let lastRefreshTimestamp = 0;

  // Utility function to get tenantId from URL or hidden input
  const getTenantIdFromUrl = () => {
    // 1. Essayer d'extraire le tenantId de l'URL
    const pathSegments = window.location.pathname.split('/');
    const tenantIdFromUrl = pathSegments[pathSegments.length - 1];
    
    if (tenantIdFromUrl && tenantIdFromUrl.length > 5) {
      console.log(`TenantId trouvé dans l'URL: ${tenantIdFromUrl}`);
      return tenantIdFromUrl;
    }
    
    // 2. Sinon, essayer de récupérer le tenantId depuis le champ caché
    const hiddenInput = document.getElementById('locataireID');
    if (hiddenInput && hiddenInput.value) {
      console.log(`TenantId trouvé dans le champ caché: ${hiddenInput.value}`);
      return hiddenInput.value;
    }
    
    // 3. Fallback au tenantId par défaut
    console.log(`Aucun tenantId trouvé, utilisation du tenantId par défaut: recv150yB0U8xsn0o`);
    return 'recv150yB0U8xsn0o'; // DEFAULT_TENANT_ID
  };

  // State to track loading status for different actions
  const [loadingStates, setLoadingStates] = useState({});

  // Centralized function to refresh data with locking mechanism
  const refreshData = async (tenantId, callback) => {
    // Vérifier si un rafraîchissement est déjà en cours ou si le dernier rafraîchissement est trop récent (moins de 1s)
    const now = Date.now();
    if (isRefreshing || (now - lastRefreshTimestamp < 1000)) {
      console.log('Rafraîchissement déjà en cours ou trop récent, ignoré');
      return;
    }
    
    // Verrouiller le rafraîchissement
    isRefreshing = true;
    lastRefreshTimestamp = now;
    
    try {
      // Exécuter le callback immédiatement pour montrer l'indicateur de chargement
      // et déclencher le rafraîchissement partiel
      if (callback && typeof callback === 'function') {
        console.log('Setting loading state and triggering partial refresh via callback...');
        callback();  // Ce callback utilisera maintenant partialRefetch
      }
      
      // Ne pas faire d'appel direct à getFinancialReport ici
      // Le callback s'en chargera avec partialRefetch
      
      // Attendre un peu pour que le serveur traite les données
      console.log(`Waiting for callback to complete...`);
      await delay(800);
      
      // Déverrouiller après le rafraîchissement
      isRefreshing = false;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données:', error);
      isRefreshing = false; // Déverrouiller même en cas d'erreur
    }
  };

  // Update invoice version (replaces window.updateFactureVersion)
  const updateFactureVersion = async (factureId, currentVersion, callback) => {
    // Store the version strings for confirmation and logging
    const fromVersion = currentVersion === 'v2' ? 'v2' : 'standard';
    const toVersion = currentVersion === 'v2' ? 'standard' : 'v2';
    
    // Pas de confirmation par pop-up
    // Continuer directement avec l'action
    
    console.log(`Starting version change for invoice: ${factureId} from ${fromVersion} to ${toVersion}`);
    setLoadingStates(prev => ({ ...prev, [`version-${factureId}`]: true }));
    
    try {
      // The API service will now handle version conversion internally
      const result = await apiService.updateInvoiceVersion(factureId, currentVersion);
      console.log(`Facture ${factureId} mise à jour avec succès: ${fromVersion} → ${toVersion}`);
      
      // Important: Wait for the API response before refreshing the data
      // This ensures the frontend is properly updated with the latest data
      
      // Récupérer le tenantId de manière robuste
      const tenantId = result?.tenantId || getTenantIdFromUrl();
      
      // Utiliser la fonction centralisée pour rafraîchir les données une seule fois
      await refreshData(tenantId, callback);
    } catch (error) {
      console.error('Erreur lors du changement de version:', error);
      console.error(`Erreur lors de la mise à jour de la version de la facture: ${error.message}`);
    } finally {
      setLoadingStates(prev => ({ ...prev, [`version-${factureId}`]: false }));
    }
  };

  // Delete invoice (replaces window.supprimerFacture)
  const supprimerFacture = async (id, version, callback) => {
    // Pas de confirmation par pop-up
    // Continuer directement avec l'action
    
    setLoadingStates(prev => ({ ...prev, [id]: true }));
    
    try {
      const result = await apiService.deleteInvoice(id, version);
      console.log('Facture supprimée avec succès');
      
      // Important: Wait for the API response before refreshing the data
      // This ensures the frontend is properly updated with the latest data
      
      // Récupérer le tenantId de manière robuste
      const tenantId = result?.tenantId || getTenantIdFromUrl();

      // Exécuter le callback immédiatement pour montrer l'indicateur de chargement
      if (callback && typeof callback === 'function') {
        console.log('Setting loading state via callback...');
        callback();
      }

      // Attendre un peu pour que le serveur traite les données
      console.log(`Waiting before refreshing data...`);
      await delay(800);

      console.log(`Refreshing financial data for tenant: ${tenantId}`);
      // Utiliser l'URL correcte via apiService
      await apiService.getFinancialReport(tenantId);

      // Refaire un appel au callback pour récupérer les données à jour
      if (callback && typeof callback === 'function') {
        console.log('Fetching updated data via callback...');
        callback();
      }
    } catch (error) {
      console.error('Erreur:', error);
      console.error('Erreur lors de la suppression de la facture: ' + error.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, [id]: false }));
    }
  };

const genererQuittances = async (bailID, callback) => {
  if (!bailID) {
    console.error("ID du bail non trouvé");
    return;
  }
  
  setLoadingStates(prev => ({ ...prev, ['quittances' + bailID]: true }));
  
  try {
    const result = await apiService.generateReceipts(bailID);
    console.log('Quittances générées avec succès!');
    
    // Exécuter le callback immédiatement pour signaler le succès
    // et laisser le composant gérer le rafraîchissement des données
    if (callback && typeof callback === 'function') {
      console.log('Exécution du callback pour mise à jour du statut...');
      callback();
    }
    
    // Pas besoin d'appeler refreshData ici, car le callback s'occupera 
    // de déclencher partialRefetch au bon moment
    
  } catch (error) {
    console.error('Erreur:', error);
    console.error('Erreur lors de la génération des quittances: ' + error.message);
    
    // Si une erreur se produit, nous pouvons appeler le callback avec un paramètre d'erreur
    if (callback && typeof callback === 'function') {
      callback(error); // Passer l'erreur au callback pour que le composant puisse réagir
    }
  } finally {
    // On ne désactive pas immédiatement le statut de chargement
    // pour permettre au callback de terminer son travail visuellement
    setTimeout(() => {
      setLoadingStates(prev => ({ ...prev, ['quittances' + bailID]: false }));
    }, 2000);
  }
};
  // Check if a specific action is loading
  const isLoading = (id) => loadingStates[id] || false;

  const value = {
    setLoadingStates,
    updateFactureVersion,
    supprimerFacture,
    genererQuittances,
    isLoading
  };

  return (
    <ActionContext.Provider value={value}>
      {children}
    </ActionContext.Provider>
  );
};

export const useActions = () => {
  const context = useContext(ActionContext);
  if (!context) {
    throw new Error('useActions doit être utilisé dans un ActionProvider');
  }
  return context;
};

export default ActionContext;
